import { doc, setDoc, collection, getDocs } from "firebase/firestore";
import type { IContentReadRepository } from "~/interfaces/IContentReadRepository";
import { ContentReadInfo, ContentReadInfoConverter } from "~/entities/ContentReadInfo";

/* Firestoreのデータ構成
/users/{userId}/plan_read/{planId}/content_types/{contentsType}
*/

const userCollectionName = "users";
const planReadCollectionName = "plan_read";
const contentTypesCollectionName = "content_types";

export class ContentReadRepository implements IContentReadRepository {
  // コンテンツタイプごとの未読情報ドキュメントを取得
  getContentReadDocQuery(userId: string, planId: string, contentsType: string) {
    const { $firestore } = useNuxtApp();
    return doc(
      $firestore,
      userCollectionName,
      userId,
      planReadCollectionName,
      planId,
      contentTypesCollectionName,
      contentsType,
    ).withConverter(ContentReadInfoConverter);
  }

  // プランごとの未読情報コレクションを取得
  getPlanContentReadCollectionQuery(userId: string, planId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      userCollectionName,
      userId,
      planReadCollectionName,
      planId,
      contentTypesCollectionName,
    ).withConverter(ContentReadInfoConverter);
  }

  async setContentReadInfo(userId: string, planId: string, contentsType: string, info: ContentReadInfo): Promise<void> {
    try {
      const docRef = this.getContentReadDocQuery(userId, planId, contentsType);
      await setDoc(docRef, info);
    } catch (error: any) {
      consoleError(`Error setting content read info: ${error.message}`, error);
      throw error;
    }
  }

  async getPlanContentReadInfo(userId: string, planId: string): Promise<{ [contentsType: string]: ContentReadInfo }> {
    try {
      const collectionRef = this.getPlanContentReadCollectionQuery(userId, planId);
      const snapshot = await getDocs(collectionRef);

      const result: { [contentsType: string]: ContentReadInfo } = {};
      snapshot.forEach(doc => {
        result[doc.id] = doc.data();
      });

      return result;
    } catch (error: any) {
      consoleWarn(`Error getting plan content read info: ${error.message}`);
      return {};
    }
  }

  async setPlanContentReadInfo(
    userId: string,
    planId: string,
    info: { [contentsType: string]: ContentReadInfo },
  ): Promise<void> {
    try {
      const promises = Object.entries(info).map(([contentsType, contentInfo]) => {
        return this.setContentReadInfo(userId, planId, contentsType, contentInfo);
      });

      await Promise.all(promises);
    } catch (error: any) {
      consoleError(`Error setting plan content read info: ${error.message}`, error);
      throw error;
    }
  }
}
